import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Pressable, TextInput } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useUpdateCameraNickname } from "@src/services/api/hooks";
import { resolveCameraDisplayName } from "@src/features/cameras/utils/camera-display-name";
import { showErrorToast } from "@src/utils/toast";
import { useCameraPermissions } from "@visioni/a3s-access/app";

interface EditableCameraNameProps {
  readonly cameraKey: string;
  readonly siteId: string;
  readonly currentNickname: string | null;
}

export const EditableCameraName = memo(function EditableCameraName({
  cameraKey,
  siteId,
  currentNickname,
}: EditableCameraNameProps) {
  const { theme } = useUnistyles();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<TextInput>(null);
  const { mutate: updateNickname, isPending } = useUpdateCameraNickname();
  const { canSetNickname } = useCameraPermissions(siteId, cameraKey);

  const displayName = useMemo(() => resolveCameraDisplayName(cameraKey, currentNickname), [cameraKey, currentNickname]);

  const handleEditPress = useCallback(() => {
    setDraft(displayName);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [displayName]);

  const handleDiscard = useCallback(() => {
    setIsEditing(false);
    setDraft("");
  }, []);

  const handleSave = useCallback(() => {
    const trimmed = draft.trim();
    const nickname = trimmed.length > 0 ? trimmed : null;
    updateNickname(
      { siteId, cameraKey, nickname },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
        onError: (error) => {
          showErrorToast({ error, description: "Failed to update camera nickname" });
        },
      },
    );
  }, [draft, siteId, cameraKey, updateNickname]);

  if (isEditing) {
    return (
      <View style={styles.editRow}>
        <View style={styles.inputWrapper}>
          <TextInput
            ref={inputRef}
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={handleSave}
            placeholder="Camera nickname"
            placeholderTextColor={theme.colors.textMuted}
            style={styles.input}
            autoFocus
            editable={!isPending}
            maxLength={100}
          />
        </View>
        <Pressable
          onPress={handleSave}
          disabled={isPending}
          style={[styles.actionButton, { backgroundColor: theme.colors.interactive.buttons.default_background }]}
          accessibilityRole="button"
          accessibilityLabel="Save nickname"
        >
          <Icon name="tick" size={18} color={theme.colors.white.default} />
        </Pressable>
        <Pressable
          onPress={handleDiscard}
          disabled={isPending}
          style={[styles.actionButton, { backgroundColor: theme.colors.surfaceCard }]}
          accessibilityRole="button"
          accessibilityLabel="Discard changes"
        >
          <Icon name="close" size={18} color={theme.colors.textMuted} />
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.displayRow}>
      <Text textStyle="lg/semibold" color="text">
        {displayName}
      </Text>
      {canSetNickname && (
        <Pressable
          onPress={handleEditPress}
          style={styles.editButton}
          accessibilityRole="button"
          accessibilityLabel="Rename camera"
        >
          <Icon name="edit" size={18} color={theme.colors.textMuted} />
        </Pressable>
      )}
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  displayRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    marginBottom: theme.spacing["3xl"],
  },
  editButton: {
    padding: theme.spacing.sm,
    borderRadius: 4,
  },
  editRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    marginBottom: theme.spacing["3xl"],
  },
  inputWrapper: {
    flex: 1,
    maxWidth: 320,
  },
  input: {
    height: 32,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: theme.spacing["2xl"],
    fontSize: 14,
    fontWeight: "400",
    outlineStyle: "none",
    color: theme.colors.text,
    borderColor: theme.colors.border,
  } as any,
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
}));
